import { type ShopSettings, settingsIssues } from '@stockwait/shared'
import { useEffect, useState } from 'react'
import { ErrorState, LoadingState } from '../components/StateViews'
import { ApiError, api } from '../lib/api'
import { useApi } from '../lib/useApi'

type Payload = {
  settings: ShopSettings
  shop: { domain: string; name: string | null; currency: string; timezone: string }
  gates: Record<string, boolean>
  defaultSendingAddress: string
}

/**
 * Settings (SW-604).
 *
 * Every control carries inline help explaining its effect **on shoppers**, not on the
 * database. A merchant setting a quantity threshold is deciding whether 4,000 people get sent
 * to a page with three units on it, and the copy says so.
 */
const SAVE_BAR_ID = 'stockwait-settings-save-bar'

export function Settings() {
  const { data, error, loading, reload } = useApi<Payload>('/settings')
  const [draft, setDraft] = useState<ShopSettings | null>(null)
  const [saving, setSaving] = useState(false)
  /**
   * Field errors the *server* rejected, keyed by dotted path.
   *
   * Kept separate from the local check below because the two can disagree: the server is the
   * authority, and a client that thinks a draft is fine when the Worker does not must still be
   * able to say which field the Worker objected to.
   */
  const [serverIssues, setServerIssues] = useState<Record<string, string>>({})

  useEffect(() => {
    if (data) setDraft(structuredClone(data.settings))
  }, [data])

  const dirty = Boolean(data && draft) && JSON.stringify(draft) !== JSON.stringify(data?.settings)

  /*
   * The contextual save bar, App Bridge's own, rendered in the admin chrome rather than in the
   * page (Built for Shopify criterion 4.1.5). It is what stops a merchant navigating away from
   * unsaved changes without answering first, which an in-page button cannot do.
   *
   * Driven from `dirty` rather than the `data-save-bar` form attribute, because that attribute
   * infers dirtiness from native form state and every input here is a Polaris web component fed
   * by React. The docs point custom state management at this API for exactly that reason.
   *
   * Shown while dirty even when the draft is invalid: hiding it would tell a merchant their
   * unsaved changes had gone somewhere. Save then refuses and marks the fields instead.
   *
   * Declared above the early returns because it is a hook, and hooks must run in the same order
   * on every render. Putting it next to the markup it drives is what broke that.
   */
  useEffect(() => {
    const saveBar = window.shopify?.saveBar
    if (!saveBar) return
    if (dirty) void saveBar.show(SAVE_BAR_ID)
    else void saveBar.hide(SAVE_BAR_ID)
  }, [dirty])

  if (loading && !data) return <LoadingState />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data || !draft) return null

  /*
   * Validated against the same schema the Worker enforces, imported rather than reimplemented
   * so the two cannot drift.
   *
   * SW-70: this used to be checked only on the server, which returned a bare 400, so the
   * merchant got "Could not save settings" and no idea which field was at fault. Clearing the
   * button text to retype it was enough to trigger it, and an App Store reviewer hit it on the
   * first save they tried. Catching it here means the field says what is wrong while they are
   * still looking at it.
   */

  const issues: Record<string, string> = { ...serverIssues }
  for (const issue of settingsIssues(draft)) issues[issue.path] = issue.message
  const blocking = Object.keys(issues).length > 0

  function patch<K extends keyof ShopSettings>(group: K, values: Partial<ShopSettings[K]>) {
    setDraft((current) =>
      current ? { ...current, [group]: { ...current[group], ...values } } : current,
    )
  }

  function discard() {
    if (!data) return
    setDraft(structuredClone(data.settings))
    setServerIssues({})
  }

  async function save() {
    if (!draft) return
    // The save bar's Save is always enabled, so validation is enforced here rather than by a
    // disabled button. Refusing with the fields marked is the honest answer to an invalid draft.
    if (blocking) {
      shopify.toast.show(
        Object.keys(issues).length === 1
          ? 'One setting needs fixing before this can save'
          : `${Object.keys(issues).length} settings need fixing before this can save`,
        { isError: true },
      )
      return
    }
    setSaving(true)
    setServerIssues({})
    try {
      const result = await api.put<{ rejected: string[] }>('/settings', draft)
      // Rejections are shown, never silently dropped: a setting that appears to save and then
      // reverts is worse than one refused with a reason.
      if (result.rejected.length > 0) {
        shopify.toast.show(`Saved. ${result.rejected.join(', ')} needs a higher plan.`, {
          isError: true,
        })
      } else {
        shopify.toast.show('Settings saved')
      }
      void window.shopify?.saveBar?.hide(SAVE_BAR_ID)
      reload()
    } catch (err) {
      // A rejected field is shown on the field itself. A toast that says only "could not save"
      // leaves the merchant hunting through six sections for the problem.
      const returned =
        err instanceof ApiError && Array.isArray(err.body.issues)
          ? (err.body.issues as { path: string; message: string }[])
          : []
      if (returned.length > 0) {
        setServerIssues(Object.fromEntries(returned.map((i) => [i.path, i.message])))
        shopify.toast.show(
          returned.length === 1
            ? 'One setting needs fixing before this can save'
            : `${returned.length} settings need fixing before this can save`,
          { isError: true },
        )
      } else {
        shopify.toast.show('Could not save settings', { isError: true })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <s-stack direction="block" gap="large">
      <s-section heading="When to send alerts">
        <s-stack direction="block" gap="base">
          <s-number-field
            label="Minimum units before alerting"
            value={String(draft.alerts.minRestockQuantity)}
            error={issues['alerts.minRestockQuantity']}
            min={1}
            details="Restocking 3 units of something 4,000 people are waiting on sends 3,997 shoppers to a sold-out page. Raise this for products with long waitlists."
            onChange={(event: Event) =>
              patch('alerts', {
                minRestockQuantity: Number((event.target as HTMLInputElement).value) || 1,
              })
            }
          />

          <s-number-field
            label="Alert only the first N shoppers"
            value={draft.alerts.notifyFirstN === null ? '' : String(draft.alerts.notifyFirstN)}
            error={issues['alerts.notifyFirstN']}
            min={1}
            disabled={!data.gates.notifyFirstN}
            details={
              data.gates.notifyFirstN
                ? 'Leave empty to alert everyone. On a small restock, telling the 20 people who waited longest beats telling 4,000 about 3 units.'
                : 'Available on the Growth plan. Alerts the people who waited longest instead of everyone.'
            }
            onChange={(event: Event) => {
              const raw = (event.target as HTMLInputElement).value
              patch('alerts', { notifyFirstN: raw === '' ? null : Number(raw) })
            }}
          />

          <s-number-field
            label="Cooldown between alerts (hours)"
            value={String(draft.alerts.variantCooldownHours)}
            error={issues['alerts.variantCooldownHours']}
            min={1}
            details="A variant that just alerted won't alert again inside this window, however its stock moves. This is what stops a flapping inventory count emailing the same shopper repeatedly."
            onChange={(event: Event) =>
              patch('alerts', {
                variantCooldownHours: Number((event.target as HTMLInputElement).value) || 24,
              })
            }
          />

          <s-number-field
            label="Daily alert cap"
            value={String(draft.alerts.dailyAlertCap)}
            error={issues['alerts.dailyAlertCap']}
            min={1}
            details="A safety net against a bulk inventory import turning into a send storm."
            onChange={(event: Event) =>
              patch('alerts', {
                dailyAlertCap: Number((event.target as HTMLInputElement).value) || 50000,
              })
            }
          />
        </s-stack>
      </s-section>

      <s-section heading="How alerts are sent">
        <s-stack direction="block" gap="base">
          <s-text-field
            label="From name"
            value={draft.sending.fromName ?? ''}
            error={issues['sending.fromName']}
            placeholder={data.shop.name ?? data.shop.domain}
            details={`Alerts send from ${data.defaultSendingAddress} with your name on them, so there's no DNS setup.`}
            onInput={(event: Event) =>
              patch('sending', { fromName: (event.target as HTMLInputElement).value || null })
            }
          />

          <s-email-field
            label="Reply-to address"
            value={draft.sending.replyTo ?? ''}
            error={issues['sending.replyTo']}
            details="Where replies go when a shopper answers an alert."
            onInput={(event: Event) =>
              patch('sending', { replyTo: (event.target as HTMLInputElement).value || null })
            }
          />

          <s-text-area
            label="Postal address"
            value={draft.sending.postalAddress ?? ''}
            error={issues['sending.postalAddress']}
            details="Required by law in the footer of every marketing email. Alerts will not send without it."
            onInput={(event: Event) =>
              patch('sending', {
                postalAddress: (event.target as HTMLTextAreaElement).value || null,
              })
            }
          />

          <s-switch
            label="Only send during set hours"
            checked={draft.sending.sendingWindow.enabled}
            details="Careful: a restock alert held for eight hours is usually worthless, because the stock is gone. Most stores should leave this off."
            onChange={(event: Event) =>
              patch('sending', {
                sendingWindow: {
                  ...draft.sending.sendingWindow,
                  enabled: (event.target as HTMLInputElement).checked,
                },
              })
            }
          />

          {draft.sending.sendingWindow.enabled ? (
            <s-banner tone="warning" heading="Alerts will be delayed">
              Shoppers outside these hours wait until the window opens, in their timezone, not
              yours. On a limited restock they will usually find it gone.
            </s-banner>
          ) : null}
        </s-stack>
      </s-section>

      <s-section heading="Consent">
        <s-stack direction="block" gap="base">
          <s-switch
            label="Require email confirmation (double opt-in)"
            checked={draft.consent.doubleOptInEmail}
            details="Shoppers must click a link before they're on the list. It roughly halves signups, so it's off by default. Turn it on if your region requires it."
            onChange={(event: Event) =>
              patch('consent', { doubleOptInEmail: (event.target as HTMLInputElement).checked })
            }
          />
          <s-text-area
            label="Consent wording shown in the signup box"
            value={draft.consent.consentCopy}
            error={issues['consent.consentCopy']}
            details="Shown under the email field. We keep a record of exactly which wording each shopper agreed to, and when."
            onInput={(event: Event) =>
              patch('consent', { consentCopy: (event.target as HTMLTextAreaElement).value })
            }
          />
        </s-stack>
      </s-section>

      <s-section heading="Button appearance">
        <s-stack direction="block" gap="base">
          <s-text-field
            label="Button text"
            value={draft.widget.buttonText}
            error={issues['widget.buttonText']}
            onInput={(event: Event) =>
              patch('widget', { buttonText: (event.target as HTMLInputElement).value })
            }
          />
          <s-stack direction="inline" gap="base">
            <s-color-field
              label="Button colour"
              value={draft.widget.buttonBackground}
              error={issues['widget.buttonBackground']}
              onChange={(event: Event) =>
                patch('widget', { buttonBackground: (event.target as HTMLInputElement).value })
              }
            />
            <s-color-field
              label="Button text colour"
              value={draft.widget.buttonTextColour}
              error={issues['widget.buttonTextColour']}
              onChange={(event: Event) =>
                patch('widget', { buttonTextColour: (event.target as HTMLInputElement).value })
              }
            />
          </s-stack>

          <s-select
            label="Where the button appears"
            value={draft.widget.placement}
            details="Automatic works on most themes. If it lands in the wrong place, use a CSS selector or drag the app block into your theme editor."
            onChange={(event: Event) =>
              patch('widget', {
                placement: (event.target as HTMLSelectElement).value as 'auto' | 'manual' | 'block',
              })
            }
          >
            <s-option value="auto">Automatic, next to Add to cart</s-option>
            <s-option value="manual">Custom CSS selector</s-option>
            <s-option value="block">Only where I place the app block</s-option>
          </s-select>

          {draft.widget.placement === 'manual' ? (
            <s-text-field
              label="CSS selector"
              value={draft.widget.manualSelector ?? ''}
              error={issues['widget.manualSelector']}
              placeholder="form[action*='/cart/add'] .product-form__buttons"
              details="The button is inserted straight after the first element matching this."
              onInput={(event: Event) =>
                patch('widget', {
                  manualSelector: (event.target as HTMLInputElement).value || null,
                })
              }
            />
          ) : null}
        </s-stack>
      </s-section>

      <s-section heading="Weekly restock digest">
        <s-switch
          label="Email me what to restock each week"
          checked={draft.intelligence.digestEnabled}
          disabled={!data.gates.weeklyDigest}
          details={
            data.gates.weeklyDigest
              ? 'A Monday summary of waiting demand and what to reorder.'
              : 'Available on the Growth plan.'
          }
          onChange={(event: Event) =>
            patch('intelligence', { digestEnabled: (event.target as HTMLInputElement).checked })
          }
        />
      </s-section>

      <ui-save-bar id={SAVE_BAR_ID}>
        <button type="button" variant="primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={discard} disabled={saving}>
          Discard
        </button>
      </ui-save-bar>

      <s-section>
        {blocking ? (
          <s-paragraph tone="critical">
            {Object.keys(issues).length === 1
              ? 'One setting above needs fixing before this can save.'
              : `${Object.keys(issues).length} settings above need fixing before this can save.`}
          </s-paragraph>
        ) : null}
      </s-section>
    </s-stack>
  )
}
